/**
 * Feature-agnostic validation engine.
 * - Reads a config map of dot-path keys to an array of rules.
 * - Evaluates built-in rule types against provided input.
 * - Skips unknown/unsupported rule types gracefully.
 * - Pure function with no side-effects.
 */

import { calculateAge } from '../calculateAge/calculateAge';
import type { EngineContext, ValidationConfigMap, ValidationRule, ValidationErrorState } from './types';
import { RuleType, EVALUATION_TYPE_COLLECTION, EVALUATION_TYPE_NESTED } from './types';

/**
 * Resolve a value from an object using dot notation.
 */
function resolvePath(obj: any, dotPath: string): { exists: boolean; value: any } {
    const segments = dotPath.split('.');
    let cur = obj;
    for (let index = 0; index < segments.length; index++) {
        // Bypass property existence check for last segment to validate undefined required fields
        if (cur && typeof cur === 'object' && ((index > 0 && index === segments.length - 1) || segments[index] in cur)) {
            cur = cur[segments[index]];
        } else {
            return { exists: false, value: undefined };
        }
    }
    return { exists: true, value: cur };
}

// Shared helper builders for creating common rules
export const makeRequired = (message: string): ValidationRule => ({ type: RuleType.REQUIRED, message });
export const makeMaxLength = (value: number, message: string): ValidationRule => ({
    type: RuleType.MAX_LENGTH,
    value,
    message
});
export const makeMinLength = (value: number, message: string): ValidationRule => ({
    type: RuleType.MIN_LENGTH,
    value,
    message
});
export const makeRegex = (pattern: RegExp, message: string): ValidationRule => ({
    type: RuleType.REGEX,
    pattern,
    message
});
export const makeCustom = (message: string, evaluator: (input: any) => boolean): ValidationRule => ({
    type: RuleType.CUSTOM,
    message,
    evaluator
});
export const makeEquals = (value: number | string, message: string): ValidationRule => ({
    type: RuleType.EQUALS,
    value,
    message
});

export const createDobValidator = (message: string, maxAge: number = 100): ValidationRule => {
    return makeCustom(message, (value: string) => {
        if (!value) return true; // Optional field

        const dob = new Date(value);
        if (isNaN(dob.getTime())) return false;

        const today = new Date();
        if (dob > today) return false; // Future date

        const age = calculateAge(dob, new Date());
        return age <= maxAge; // Within max age limit
    });
};

// Built-in rule evaluators
const evaluators = {
    [RuleType.REQUIRED]: (value: any) => value !== undefined && value !== null && String(value).trim() !== '',
    [RuleType.MAX_LENGTH]: (value: any, rule: ValidationRule) =>
        typeof value === 'string' && typeof rule.value === 'number' ? value.length <= rule.value : true,
    [RuleType.MIN_LENGTH]: (value: any, rule: ValidationRule) =>
        typeof value === 'string' && typeof rule.value === 'number' ? value.length >= rule.value : true,
    [RuleType.REGEX]: (value: any, rule: ValidationRule) =>
        typeof value === 'string' && value && rule.pattern instanceof RegExp ? rule.pattern.test(value) : true,
    [RuleType.DISTINCT_VALUES]: (value: any) => {
        // Expect the field value to be an array of values
        const values = (Array.isArray(value) ? (value as unknown[]) : [])
            .filter((e) => typeof e === 'string')
            .map((e) => (e as string).toLowerCase().trim())
            .filter((e) => e.length > 0);

        // If 0 or 1 meaningful values, uniqueness holds; presence can be enforced via REQUIRED separately.
        if (values.length < 2) return true;

        const unique = new Set(values);
        return unique.size === values.length;
    },
    [RuleType.EQUALS]: (value: any, rule: ValidationRule) => {
        return value === rule.value;
    },
    [RuleType.CUSTOM]: (value: any, rule: ValidationRule) => {
        return rule.evaluator && rule.evaluator(value);
    }
} as const;

/**
 * Validate inputs against a config map. Returns error messages.
 * Unknown rule types are ignored (not considered errors).
 */
export function validate(
    input: Record<string, any>,
    config: ValidationConfigMap,
    context: EngineContext = {},
    errorPrefix?: string,
    index?: number
): ValidationErrorState {
    const errors: ValidationErrorState = [];

    for (const fieldKey of Object.keys(config)) {
        const fieldConfig = config[fieldKey];
        const { exists, value } = resolvePath(input, fieldKey);

        // If field path doesn't exist in input, skip its validations
        if (!exists) continue;

        if (fieldConfig.evaluationType) {
            if (!fieldConfig.config || value === null || value === undefined) continue;

            if (fieldConfig.evaluationType === EVALUATION_TYPE_COLLECTION) {
                if (Array.isArray(value)) {
                    value.forEach((item, index) => {
                        if (item && typeof item === 'object') {
                            errors.push(
                                ...validate(
                                    item as Record<string, any>,
                                    fieldConfig.config!,
                                    context,
                                    `${fieldConfig.errorPrefix} ${(item.index || index) + 1}`,
                                    index
                                )
                            );
                        }
                    });
                }
            }
            if (fieldConfig.evaluationType === EVALUATION_TYPE_NESTED) {
                if (typeof value === 'object') {
                    errors.push(...validate(value as Record<string, any>, fieldConfig.config!, context, errorPrefix, index));
                }
            }
            continue;
        }

        // Treat as an array of rules (simple field validations)
        const rules = fieldConfig.rules;
        if (!rules || !Array.isArray(rules)) {
            // No rules to validate
            continue;
        }

        for (const rule of rules) {
            // Optional contextual constraints
            if (rule.excludeOfferTypes?.includes(context.offerType as string)) continue;
            if (rule.excludeFuelTypes?.includes(context.fuelType as string)) continue;

            if (rule.applicableStates) {
                const st = context.buyerState as string | undefined;
                if (!st || !rule.applicableStates.includes(st)) continue;
            }

            // Optional conditional predicate: only apply rule when condition passes
            if (typeof rule.when === 'function') {
                try {
                    const shouldApply = rule.when({ ...input, ...context });
                    if (!shouldApply) continue;
                } catch {
                    // On predicate error, skip applying the rule to avoid breaking validation flow
                    continue;
                }
            }

            const evaluator = evaluators[rule.type as RuleType];
            if (!evaluator) {
                // Skip unsupported rule types
                continue;
            }

            const ok = evaluator(value, rule as any);
            if (!ok) {
                const { fieldName, objectType } = fieldConfig;
                const errorMessage = errorPrefix ? `${errorPrefix} ${rule.message}` : rule.message;

                if (fieldName && objectType) {
                    errors.push({
                        objectType,
                        fieldName,
                        errors: [errorMessage],
                        ...(index !== undefined && { index })
                    });
                } else {
                    errors.push(errorMessage);
                }
            }
        }
    }
    return errors;
}
